import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import { Plus, Save, X, Edit, Trash2, Info, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';

const SurfaceItems = () => {
  const [items, setItems] = useState([]);
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', icon_id: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [itemsRes, iconsRes] = await Promise.all([
        api.get('/surface-items'),
        api.get('/icons'),
      ]);
      setItems(itemsRes.data);
      setIcons(iconsRes.data);
    } catch (error) {
      toast.error('Echec du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Surface icons only (the canonical source for new surfaces)
  const surfaceIcons = useMemo(
    () => icons.filter((i) => (i.category || '').toLowerCase() === 'surface'),
    [icons]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // zone_id is intentionally omitted: surfaces are zone-agnostic.
      const payload = {
        name: formData.name,
        description: formData.description,
        icon_id: formData.icon_id || null,
      };
      if (editingId) {
        await api.put(`/surface-items/${editingId}`, payload);
        toast.success('Surface modifiee');
      } else {
        await api.post('/surface-items', payload);
        toast.success('Surface creee');
      }
      resetForm();
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name,
      description: item.description || '',
      icon_id: item.icon_id || '',
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette surface ?')) return;
    try {
      await api.delete(`/surface-items/${id}`);
      toast.success('Surface supprimee');
      loadData();
    } catch (error) {
      toast.error('Echec de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', icon_id: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const getIconPreview = (iconId) => {
    const icon = icons.find((i) => i.id === iconId);
    if (!icon || !icon.image_url) {
      return (
        <span data-testid="surface-no-icon" className="inline-flex items-center justify-center w-8 h-8 rounded"
          style={{ backgroundColor: '#F3F4F6', color: '#9CA3AF' }}>
          <ImageIcon size={14} />
        </span>
      );
    }
    return <img src={BACKEND_BASE + icon.image_url} alt={icon.name} className="w-8 h-8 object-contain inline-block" />;
  };

  if (loading) return <Layout><div className="text-center py-12">Chargement...</div></Layout>;

  return (
    <Layout>
      <div data-testid="surface-items-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
            Surfaces
          </h1>
          {!showForm && (
            <button onClick={() => setShowForm(true)} data-testid="add-surface-item-button"
              className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
              style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <Plus size={20} /><span>Ajouter Surface</span>
            </button>
          )}
        </div>

        <div data-testid="surface-info-banner"
          className="flex items-start gap-3 mb-6 px-4 py-3 rounded border"
          style={{ borderColor: '#BFDBFE', backgroundColor: '#EFF6FF', color: '#1E3A8A' }}>
          <Info size={18} className="mt-0.5 flex-shrink-0" />
          <div className="text-sm leading-5">
            Les surfaces sont synchronisees automatiquement avec les icones de categorie <strong>Surface</strong>
            (Parametrage &gt; Icones). Toute icone ajoutee, modifiee ou supprimee dans cette categorie met a jour
            la liste ci-dessous. La liaison entre une surface et une zone se fait au moment de la creation du plan HACCP.
          </div>
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow p-6 border mb-8" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#0E2D43' }}>
              {editingId ? 'Modifier Surface' : 'Nouvelle Surface'}
            </h2>
            <form onSubmit={handleSubmit} data-testid="surface-item-form">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Nom *</label>
                  <input type="text" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="surface-item-name-input"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: Plan de travail, Plaque de cuisson" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Icone</label>
                  <select value={formData.icon_id}
                    onChange={(e) => setFormData({ ...formData, icon_id: e.target.value })}
                    data-testid="surface-item-icon-select"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}>
                    <option value="">-- Aucune icone --</option>
                    {surfaceIcons.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Description</label>
                  <input type="text" value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="surface-item-description-input"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                    placeholder="Description optionnelle" />
                </div>
              </div>
              <div className="flex space-x-3">
                <button type="submit" data-testid="save-surface-item-button"
                  className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
                  style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                  <Save size={18} /><span>Enregistrer</span>
                </button>
                <button type="button" onClick={resetForm}
                  className="flex items-center space-x-2 px-4 py-2 rounded border"
                  style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}>
                  <X size={18} /><span>Annuler</span>
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
          <table className="w-full">
            <thead style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium">Icone</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Nom</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Description</th>
                <th className="px-6 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="4" className="px-6 py-8 text-center" style={{ color: '#6B7280' }}>
                  Aucune surface. Creez une icone de categorie &quot;Surface&quot; pour la voir apparaitre ici.
                </td></tr>
              ) : items.map((item) => (
                <tr key={item.id} data-testid={`surface-item-row-${item.id}`} className="border-t" style={{ borderColor: '#E5E7EB' }}>
                  <td className="px-6 py-4">{getIconPreview(item.icon_id)}</td>
                  <td className="px-6 py-4 font-medium" style={{ color: '#0E2D43' }}>{item.name}</td>
                  <td className="px-6 py-4" style={{ color: '#6B7280' }}>{item.description || '-'}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => handleEdit(item)} data-testid={`edit-surface-${item.id}`}
                      className="inline-flex items-center px-3 py-1 rounded mr-2"
                      style={{ backgroundColor: '#F3F4F6', color: '#0E2D43' }}><Edit size={16} /></button>
                    <button onClick={() => handleDelete(item.id)} data-testid={`delete-surface-${item.id}`}
                      className="inline-flex items-center px-3 py-1 rounded"
                      style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default SurfaceItems;
